import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Mail, X, Send } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { v: "contributor", l: "Contributor", d: "Can add/edit records" },
  { v: "viewer", l: "Viewer", d: "Read-only" },
  { v: "professional", l: "Professional", d: "Care coordinator / advocate" },
];

export default function MembersPanel({ chestId }) {
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("contributor");
  const [busy, setBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/hope-chests/${chestId}/members`);
      setMembers(data);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chestId]);

  const invite = async () => {
    if (!email) { toast.error("Email is required"); return; }
    setBusy(true);
    try {
      await api.post(`/hope-chests/${chestId}/members`, { email, role, app_origin: window.location.origin });
      toast.success("Invitation sent");
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not invite");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this person from the Hope Chest?")) return;
    try {
      await api.delete(`/hope-chests/${chestId}/members/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Could not remove"); }
  };

  const sendWeeklyReport = async () => {
    setReportBusy(true);
    try {
      const { data } = await api.post(`/hope-chests/${chestId}/weekly-report/send`);
      toast.success(`Weekly report sent to ${data.sent} of ${data.recipients} recipient${data.recipients !== 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send report");
    } finally { setReportBusy(false); }
  };

  return (
    <div>
      <div className="hc-card p-6 mb-4">
        <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
          <h3 className="font-display text-lg">Invite family or a care professional</h3>
          <Button onClick={sendWeeklyReport} disabled={reportBusy} variant="outline" size="sm" className="rounded-full border-[#EBE5DF]" data-testid="send-weekly-report-button">
            <Send className="w-3.5 h-3.5 mr-2" /> {reportBusy ? "Sending…" : "Send weekly report now"}
          </Button>
        </div>
        <p className="text-sm text-[#7A736E] mb-4">They'll receive a warm email letting them know you've shared this Hope Chest with them. Weekly reports automatically go to the whole team every Sunday.</p>
        <div className="grid sm:grid-cols-12 gap-3 items-end">
          <div className="sm:col-span-5">
            <Label>Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="sibling@example.com" className="rounded-xl mt-1.5" data-testid="invite-email" />
          </div>
          <div className="sm:col-span-4">
            <Label>Role</Label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full mt-1.5 rounded-xl border border-[#EBE5DF] px-3 py-2 bg-white" data-testid="invite-role">
              {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l} — {r.d}</option>)}
            </select>
          </div>
          <div className="sm:col-span-3">
            <Button onClick={invite} disabled={busy} className="w-full rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="invite-submit">
              <Mail className="w-4 h-4 mr-2" /> Send invite
            </Button>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {members.length === 0 && <p className="text-sm text-[#7A736E]">No one else has access yet.</p>}
        {members.map((m) => (
          <div key={m.id} className="hc-card p-4 flex items-center justify-between" data-testid={`member-${m.id}`}>
            <div>
              <p className="text-sm font-medium">{m.invited_email}</p>
              <p className="text-xs text-[#7A736E] capitalize">{m.role} • {m.user_id ? "Joined" : "Invited"}</p>
            </div>
            <button onClick={() => remove(m.id)} className="text-[#7A736E] hover:text-[#C87B67]" data-testid={`member-remove-${m.id}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
